export interface ConnectionSnippet {
  label: string;
  language: string;
  code: string;
}

type CredValues = {
  host: string;
  port: string;
  username: string;
  password: string;
  database: string;
};

function fill(template: string, vals: CredValues): string {
  return template
    .replace(/\{host\}/g, vals.host || "HOST")
    .replace(/\{port\}/g, vals.port || "PORT")
    .replace(/\{username\}/g, vals.username || "USER")
    .replace(/\{password\}/g, vals.password || "PASSWORD")
    .replace(/\{database\}/g, vals.database || "DATABASE");
}

const SNIPPETS: Record<string, (v: CredValues) => ConnectionSnippet[]> = {
  mysql: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill("mysql -h {host} -P {port} -u {username} -p{password}", v),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `import mysql.connector
conn = mysql.connector.connect(
    host="{host}", port={port},
    user="{username}", password="{password}",
    database="{database}"
)`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const mysql = require('mysql2/promise');
const conn = await mysql.createConnection({
  host: '{host}', port: {port},
  user: '{username}', password: '{password}',
  database: '{database}'
});`,
        v
      ),
    },
  ],

  mariadb: (v) => SNIPPETS.mysql(v),

  postgresql: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill(
        "PGPASSWORD={password} psql -h {host} -p {port} -U {username} -d {database}",
        v
      ),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `import psycopg2
conn = psycopg2.connect(
    host="{host}", port={port},
    user="{username}", password="{password}",
    dbname="{database}"
)`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { Pool } = require('pg');
const pool = new Pool({
  host: '{host}', port: {port},
  user: '{username}', password: '{password}',
  database: '{database}'
});`,
        v
      ),
    },
  ],

  postgres: (v) => SNIPPETS.postgresql(v),
  timescaledb: (v) => SNIPPETS.postgresql(v),
  pgvector: (v) => SNIPPETS.postgresql(v),

  redis: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill("redis-cli -h {host} -p {port} -a {password}", v),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `import redis
r = redis.Redis(host="{host}", port={port}, password="{password}", decode_responses=True)`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { createClient } = require('redis');
const client = createClient({ url: 'redis://:{password}@{host}:{port}' });
await client.connect();`,
        v
      ),
    },
  ],

  mongodb: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill(
        "mongosh mongodb://{username}:{password}@{host}:{port}/{database}",
        v
      ),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `from pymongo import MongoClient
client = MongoClient("mongodb://{username}:{password}@{host}:{port}/")
db = client["{database}"]`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { MongoClient } = require('mongodb');
const client = new MongoClient('mongodb://{username}:{password}@{host}:{port}/');
await client.connect();
const db = client.db('{database}');`,
        v
      ),
    },
  ],

  elasticsearch: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill("curl http://{host}:{port}/_cat/indices?v", v),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `from elasticsearch import Elasticsearch
es = Elasticsearch("http://{host}:{port}")
print(es.info())`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { Client } = require('@elastic/elasticsearch');
const client = new Client({ node: 'http://{host}:{port}' });`,
        v
      ),
    },
  ],

  couchdb: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill(
        "curl -u {username}:{password} http://{host}:{port}/_all_dbs",
        v
      ),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `import couchdb
couch = couchdb.Server("http://{username}:{password}@{host}:{port}/")
db = couch["{database}"]`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const nano = require('nano');
const db = nano('http://{username}:{password}@{host}:{port}').use('{database}');`,
        v
      ),
    },
  ],

  influxdb: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill(
        `influx config create \\
  --config-name avahana \\
  --host-url http://{host}:{port} \\
  --token {password} \\
  --org {username}`,
        v
      ),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `from influxdb_client import InfluxDBClient
client = InfluxDBClient(
    url="http://{host}:{port}",
    token="{password}",
    org="{username}"
)`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { InfluxDB } = require('@influxdata/influxdb-client');
const client = new InfluxDB({
  url: 'http://{host}:{port}',
  token: '{password}'
});`,
        v
      ),
    },
  ],

  neo4j: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill(
        "cypher-shell -a bolt://{host}:{port} -u {username} -p {password}",
        v
      ),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `from neo4j import GraphDatabase
driver = GraphDatabase.driver(
    "bolt://{host}:{port}",
    auth=("{username}", "{password}")
)`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const neo4j = require('neo4j-driver');
const driver = neo4j.driver(
  'bolt://{host}:{port}',
  neo4j.auth.basic('{username}', '{password}')
);`,
        v
      ),
    },
  ],

  minio: (v) => [
    {
      label: "mc CLI",
      language: "bash",
      code: fill(
        `mc alias set myminio http://{host}:{port} {username} {password}
mc ls myminio/`,
        v
      ),
    },
    {
      label: "Python (boto3 / AWS SDK)",
      language: "python",
      code: fill(
        `import boto3
s3 = boto3.client(
    "s3",
    endpoint_url="http://{host}:{port}",
    aws_access_key_id="{username}",
    aws_secret_access_key="{password}",
)
print(s3.list_buckets())`,
        v
      ),
    },
    {
      label: "Node.js (AWS SDK v3)",
      language: "javascript",
      code: fill(
        `const { S3Client, ListBucketsCommand } = require('@aws-sdk/client-s3');
const client = new S3Client({
  endpoint: 'http://{host}:{port}',
  credentials: { accessKeyId: '{username}', secretAccessKey: '{password}' },
  region: 'us-east-1', forcePathStyle: true
});`,
        v
      ),
    },
  ],

  "object-storage": (v) => SNIPPETS.minio(v),

  qdrant: (v) => [
    {
      label: "CLI",
      language: "bash",
      code: fill("curl http://{host}:{port}/collections", v),
    },
    {
      label: "Python",
      language: "python",
      code: fill(
        `from qdrant_client import QdrantClient
client = QdrantClient(host="{host}", port={port})`,
        v
      ),
    },
    {
      label: "Node.js",
      language: "javascript",
      code: fill(
        `const { QdrantClient } = require('@qdrant/js-client-rest');
const client = new QdrantClient({ host: '{host}', port: {port} });`,
        v
      ),
    },
  ],
};

export function getConnectionSnippets(
  framework: string,
  credFields: Array<{ key: string; value?: string }>
): ConnectionSnippet[] {
  const key = (framework || "").toLowerCase();
  const fn = SNIPPETS[key];
  if (!fn) return [];

  const vals: CredValues = {
    host: "",
    port: "",
    username: "",
    password: "",
    database: "",
  };
  for (const f of credFields) {
    if (f.key in vals) (vals as any)[f.key] = f.value || "";
  }

  return fn(vals);
}
